import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Autocomplete,
  Grid,
  Card,
  CardContent,
  CardActions,
  Modal,
  Paper,
  Switch,
  FormControlLabel,
  Chip,
  IconButton,
  Badge,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import SearchIcon from "@mui/icons-material/Search";
import PreviewIcon from "@mui/icons-material/Preview";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import DeleteIcon from "@mui/icons-material/Delete";
import PriceChangeIcon from "@mui/icons-material/PriceChange";
import { IProduct, IResultPagination, ComponentModal } from "../../common/interfaces";
import { CommonService } from "../../services/CommonService";
import { request } from "../../services/AjaxService";
import { useModalStore } from "../../context/modalStore";

interface ProductLabelsModalProps {
  open: boolean;
  onClose: () => void;
}

export const ProductLabelsModal: React.FC<ProductLabelsModalProps> = ({
  open,
  onClose,
}) => {
  const modalManager = useModalStore();
  const [searchTerm, setSearchTerm] = useState("");
  const [products, setProducts] = useState<IProduct[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<IProduct | null>(null);
  const [loading, setLoading] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewProduct, setPreviewProduct] = useState<IProduct | null>(null);
  const [selectedProducts, setSelectedProducts] = useState<Array<{product: IProduct, quantity: number}>>([]);
  const [showMultipleMode, setShowMultipleMode] = useState(false);

  // Buscar productos
  const searchProducts = async (query: string) => {
    if (query.length < 2) {
      setProducts([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response: IResultPagination<IProduct> = await request.get(
        `products?search=${encodeURIComponent(query)}`
      );

      if (response.data) {
        setProducts(response.data);
      } else {
        setError("Error al buscar productos");
        setProducts([]);
      }
    } catch (err) {
      setError("Error de conexión al buscar productos");
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  // Efecto para buscar productos cuando cambia el término de búsqueda
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchProducts(searchTerm);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Abrir modal de precios
  const openPriceUpdatesModal = () => {
    onClose(); // Cerrar el modal actual
    modalManager.openModal(ComponentModal.PriceUpdates);
  };

  // Vista previa de etiqueta
  const showPreview = (product: IProduct) => {
    console.log("showPreview called with product:", product); // Debug log
    setPreviewProduct(product);
    setPreviewOpen(true);
  };

  // Agregar producto a la lista de impresión múltiple
  const addToMultiplePrint = (product: IProduct) => {
    const existing = selectedProducts.find(p => p.product.id === product.id);
    if (existing) {
      setSelectedProducts(prev =>
        prev.map(p => p.product.id === product.id ? {...p, quantity: p.quantity + 1} : p)
      );
    } else {
      setSelectedProducts(prev => [...prev, {product, quantity: 1}]);
    }
    setSuccess(`"${product.name}" agregado a la lista de impresión`);
  };

  // Remover producto de la lista
  const removeFromMultiplePrint = (productId: number) => {
    setSelectedProducts(prev => prev.filter(p => p.product.id !== productId));
  };

  // Cambiar cantidad
  const updateQuantity = (productId: number, quantity: number) => {
    if (quantity <= 0) {
      removeFromMultiplePrint(productId);
      return;
    }
    setSelectedProducts(prev =>
      prev.map(p => p.product.id === productId ? {...p, quantity} : p)
    );
  };

  // Imprimir etiqueta individual
  const printLabel = async (product: IProduct) => {
    setPrinting(true);
    setError(null);
    setSuccess(null);

    try {
      // Usar servidor mock o impresión real
      const useMockServer = window.location.hostname === 'localhost' && process.env.NODE_ENV === 'development';

      if (useMockServer) {
        console.log("🖨️ USANDO SERVIDOR MOCK PARA IMPRESIÓN:");
        console.log("Producto:", product.name);
        console.log("ID:", product.id);
        console.log("Precio:", product.price_iva || product.price_1);
        console.log("Código de barras:", product.barcode);
      }

      // Impresión real (que usará el servidor mock si está corriendo)
      await CommonService.printProductLabel(product.id);
      setSuccess(`Etiqueta de "${product.name}" enviada a impresión`);
    } catch (err) {
      setError("Error al imprimir la etiqueta");
      console.error("Error printing label:", err);
    } finally {
      setPrinting(false);
    }
  };

  // Imprimir múltiples etiquetas
  const printMultipleLabels = async () => {
    if (selectedProducts.length === 0) {
      setError("No hay productos seleccionados para imprimir");
      return;
    }

    setPrinting(true);
    setError(null);
    setSuccess(null);

    try {
      const products = selectedProducts.map(p => ({
        id: p.product.id,
        quantity: p.quantity
      }));

      // Usar servidor mock o impresión real
      const useMockServer = window.location.hostname === 'localhost' && process.env.NODE_ENV === 'development';

      if (useMockServer) {
        console.log("🖨️ USANDO SERVIDOR MOCK PARA IMPRESIÓN MÚLTIPLE:");
        console.log("Productos a imprimir:", products);
        selectedProducts.forEach(p => {
          console.log(`- ${p.product.name} (x${p.quantity})`);
        });
      }

      // Impresión real (que usará el servidor mock si está corriendo)
      await CommonService.printMultipleProductLabels(products);
      const totalLabels = selectedProducts.reduce((sum, p) => sum + p.quantity, 0);
      setSuccess(`${totalLabels} etiquetas enviadas a impresión`);

      setSelectedProducts([]);
    } catch (err) {
      setError("Error al imprimir las etiquetas");
      console.error("Error printing multiple labels:", err);
    } finally {
      setPrinting(false);
    }
  };

  // Limpiar estado al cerrar
  const handleClose = () => {
    setSearchTerm("");
    setProducts([]);
    setSelectedProduct(null);
    setError(null);
    setSuccess(null);
    setPreviewOpen(false);
    setPreviewProduct(null);
    setSelectedProducts([]);
    setShowMultipleMode(false);
    onClose();
  };

  // Componente de vista previa
  const LabelPreview = ({ product }: { product: IProduct }) => {
    console.log("LabelPreview product:", product); // Debug log

    if (!product) {
      return (
        <Box
          sx={{
            width: 300,
            height: 400,
            bgcolor: "white",
            border: "2px dashed #ccc",
            borderRadius: 2,
            p: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography color="error">No hay datos del producto</Typography>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          width: "300px !important",
          height: "400px !important",
          bgcolor: "#ffffff !important",
          backgroundColor: "#ffffff !important",
          border: "2px dashed #333 !important",
          borderRadius: "8px !important",
          p: "24px !important",
          display: "flex !important",
          flexDirection: "column !important",
          alignItems: "center !important",
          justifyContent: "center !important",
          fontFamily: "monospace !important",
          fontSize: "14px !important",
          lineHeight: "1.2 !important",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1) !important",
          color: "#000000 !important",
          opacity: "1 !important",
        }}
        style={{
          backgroundColor: "#ffffff",
          color: "#000000",
          width: "300px",
          height: "400px",
        }}
      >
        {/* Nombre del producto */}
        <Typography
          variant="h6"
          sx={{
            fontWeight: "bold !important",
            textAlign: "center",
            mb: 2,
            fontSize: "16px !important",
            fontFamily: "monospace !important",
            color: "#000000 !important",
            textShadow: "none !important",
            opacity: "1 !important",
          }}
          style={{
            color: "#000000",
            fontWeight: "bold",
            fontSize: "16px",
          }}
        >
          {product.name ? product.name.toUpperCase() : "SIN NOMBRE"}
        </Typography>

        {/* Precio */}
        <Typography
          variant="h4"
          sx={{
            fontWeight: "bold !important",
            fontSize: "24px !important",
            fontFamily: "monospace !important",
            color: "#000000 !important",
            textShadow: "none !important",
            opacity: "1 !important",
          }}
          style={{
            color: "#000000",
            fontWeight: "bold",
            fontSize: "24px",
          }}
        >
          ${(Number(product.price_iva) || Number(product.price_1) || 0).toFixed(2)}
        </Typography>
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <PrintIcon />
            <Typography variant="h6">Etiquetas de Productos</Typography>
            {selectedProducts.length > 0 && (
              <Badge badgeContent={selectedProducts.length} color="primary">
                <Chip label="Múltiple" size="small" color="secondary" />
              </Badge>
            )}
          </Box>
          <Box display="flex" alignItems="center" gap={2}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<PriceChangeIcon />}
              onClick={openPriceUpdatesModal}
              sx={{
                minWidth: 'auto',
                fontSize: '0.75rem',
                px: 1.5,
                whiteSpace: 'nowrap'
              }}
            >
              Actualización de Precios
            </Button>
            <FormControlLabel
              control={
                <Switch
                  checked={showMultipleMode}
                  onChange={(e) => setShowMultipleMode(e.target.checked)}
                  color="primary"
                />
              }
              label="Modo Múltiple"
            />
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            label=""
            placeholder="Buscar producto por nombre, código de barras o ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: "action.active" }} />,
              endAdornment: loading && <CircularProgress size={20} />,
            }}
            InputLabelProps={{
              shrink: true,
            }}
            sx={{
              mb: 2,
              '& .MuiInputLabel-root': {
                backgroundColor: 'transparent',
                zIndex: 1,
              },
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.23)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'primary.main',
                },
              },
            }}
          />

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}
        </Box>

        {/* Lista de productos seleccionados para impresión múltiple */}
        {showMultipleMode && selectedProducts.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Productos seleccionados ({selectedProducts.reduce((sum, p) => sum + p.quantity, 0)} etiquetas):
            </Typography>
            <Grid container spacing={2}>
              {selectedProducts.map(({product, quantity}) => (
                <Grid item xs={12} sm={6} md={4} key={product.id}>
                  <Card variant="outlined">
                    <CardContent sx={{ pb: 1 }}>
                      <Typography variant="subtitle2" noWrap>
                        {product.name}
                      </Typography>
                      <Typography variant="h6" color="primary">
                        ${(Number(product.price_iva) || Number(product.price_1) || 0).toFixed(2)}
                      </Typography>
                      <Box display="flex" alignItems="center" justifyContent="space-between" mt={1}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <IconButton
                            size="small"
                            onClick={() => updateQuantity(product.id, quantity - 1)}
                          >
                            <RemoveIcon />
                          </IconButton>
                          <Typography variant="body1" sx={{ minWidth: 20, textAlign: "center" }}>
                            {quantity}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => updateQuantity(product.id, quantity + 1)}
                          >
                            <AddIcon />
                          </IconButton>
                        </Box>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => removeFromMultiplePrint(product.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
            <Box mt={2} display="flex" justifyContent="center">
              <Button
                variant="contained"
                size="large"
                startIcon={<PrintIcon />}
                onClick={printMultipleLabels}
                disabled={printing}
              >
                {printing ? "Imprimiendo..." : `Imprimir ${selectedProducts.reduce((sum, p) => sum + p.quantity, 0)} Etiquetas`}
              </Button>
            </Box>
          </Box>
        )}

        {products.length > 0 && (
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Productos encontrados:
            </Typography>
            <Grid container spacing={3}>
              {products.map((product) => (
                <Grid item xs={12} sm={6} md={4} key={product.id}>
                  <Card
                    variant="outlined"
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      transition: "box-shadow 0.2s",
                      "&:hover": {
                        boxShadow: 2
                      }
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                      <Typography
                        variant="h6"
                        component="div"
                        noWrap
                        sx={{
                          fontSize: "1rem",
                          fontWeight: "bold",
                          mb: 1
                        }}
                      >
                        {product.name}
                      </Typography>
                      <Typography
                        color="text.secondary"
                        variant="body2"
                        sx={{ mb: 0.5 }}
                      >
                        ID: {product.id}
                      </Typography>
                      {product.barcode && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 1 }}
                        >
                          Código: {product.barcode.trim()}
                        </Typography>
                      )}
                      <Typography
                        variant="h6"
                        color="primary"
                        sx={{
                          mt: 1,
                          fontSize: "1.1rem",
                          fontWeight: "bold"
                        }}
                      >
                        ${product.price_iva ? Number(product.price_iva).toFixed(2) : Number(product.price_1).toFixed(2)}
                      </Typography>
                    </CardContent>
                    <CardActions sx={{ p: 2, pt: 1 }}>
                      <Box sx={{ width: "100%" }}>
                        {/* Primera fila de botones */}
                        <Box sx={{ display: "flex", gap: 1, mb: showMultipleMode ? 1 : 0 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<PreviewIcon />}
                            onClick={() => showPreview(product)}
                            sx={{
                              flex: 1,
                              minHeight: "36px",
                              fontSize: "0.75rem"
                            }}
                          >
                            Vista Previa
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<PrintIcon />}
                            onClick={() => printLabel(product)}
                            disabled={printing}
                            sx={{
                              flex: 1,
                              minHeight: "36px",
                              fontSize: "0.75rem"
                            }}
                          >
                            {printing ? "Imprimiendo..." : "Imprimir"}
                          </Button>
                        </Box>

                        {/* Segunda fila solo en modo múltiple */}
                        {showMultipleMode && (
                          <Box sx={{ display: "flex", justifyContent: "center" }}>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<AddIcon />}
                              onClick={() => addToMultiplePrint(product)}
                              color="secondary"
                              sx={{
                                minWidth: "120px",
                                minHeight: "36px",
                                fontSize: "0.75rem"
                              }}
                            >
                              Agregar
                            </Button>
                          </Box>
                        )}
                      </Box>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {searchTerm.length >= 2 && products.length === 0 && !loading && (
          <Box textAlign="center" py={4}>
            <Typography color="text.secondary">
              No se encontraron productos con el término "{searchTerm}"
            </Typography>
          </Box>
        )}

        {searchTerm.length < 2 && (
          <Box textAlign="center" py={4}>
            <Typography color="text.secondary">
              Ingrese al menos 2 caracteres para buscar productos
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cerrar</Button>
      </DialogActions>

      {/* Modal de Vista Previa */}
      <Modal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        aria-labelledby="preview-modal-title"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            outline: "none",
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" id="preview-modal-title">
              Vista Previa de Etiqueta
            </Typography>
            <Button
              onClick={() => setPreviewOpen(false)}
              startIcon={<CloseIcon />}
              size="small"
            >
              Cerrar
            </Button>
          </Box>

          {previewProduct && <LabelPreview product={previewProduct} />}

          <Box mt={3} display="flex" justifyContent="center" gap={2}>
            <Button
              variant="outlined"
              onClick={() => setPreviewOpen(false)}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              startIcon={<PrintIcon />}
              onClick={() => {
                if (previewProduct) {
                  printLabel(previewProduct);
                  setPreviewOpen(false);
                }
              }}
              disabled={printing}
            >
              {printing ? "Imprimiendo..." : "Imprimir Etiqueta"}
            </Button>
          </Box>
        </Box>
      </Modal>
    </Dialog>
  );
};
